import React, { memo } from 'react'
import { Modal } from 'react-bootstrap'
import { useTheme } from 'styled-components'

import { IBarrierType, IBarrierTypeIcon } from 'plugin-codaf-risk-ext-bowtie/types'

import { RoundedModal, Scrollbars } from '../../../../../generic'
import { useBowtieLanguage } from '../../../../../hooks'
import { Icon } from '../../../../../utils'
import { ActionIcons } from '../ActionButtons/ActionIcons'
import { BarrierIcons, ModalBody, ModalCloseButton } from './styles'

interface Props {
    show: boolean
    onClose: () => void
    extraInfo?: string
    barrierTypeIconMap: Map<number, IBarrierTypeIcon>
    barrierTypes: IBarrierType[]
}

const HelpModal: React.FC<Props> = ({ show, onClose, extraInfo = null, barrierTypeIconMap, barrierTypes }) => {
    const i18n = useBowtieLanguage()
    const HEIGHT = 520
    const theme = useTheme()

    const BARRIER_ITEMS = [
        {
            item: <Icon type="critical" withG={false} style={{ transform: 'scale(1.5)' }} />,
            description: i18n('High criticality')
        },
        {
            item: <Icon type="manual" style={{ transform: 'scale(1.5)' }} />,
            description: i18n('Manual Barrier Status Interaction')
        },
        {
            item: <Icon type="mandatory" style={{ transform: 'scale(1.5)' }} />,
            description: i18n('Mandatory Barrier')
        }
    ]

    const ACTIONS = [
        {
            item: <ActionIcons type="language" />,
            description: i18n('Toggle language')
        },
        {
            item: <ActionIcons type="help" />,
            description: i18n('Open help modal')
        },
        {
            item: <ActionIcons type="highlight" />,
            description: i18n('Highlight inactive and unknown barriers')
        },
        {
            item: <ActionIcons type="open" />,
            description: i18n('Open first barriers level')
        },
        {
            item: <ActionIcons type="close" />,
            description: i18n('Close all barriers')
        },
        {
            item: <ActionIcons type="zoomIn" />,
            description: i18n('Zoom in')
        },
        {
            item: <ActionIcons type="zoomOut" />,
            description: i18n('Zoom out')
        },

        {
            item: <ActionIcons type="reset" />,
            description: i18n('Reset zoom and position')
        },
        {
            item: <i className="far fa-edit"></i>,
            description: i18n('Edit this bowtie')
        },
        {
            item: <i className="far fa-cog"></i>,
            description: i18n('Settings')
        }
    ]

    return (
        <RoundedModal show={show} onHide={onClose}>
            <Modal.Header>
                <Modal.Title>{i18n('Help')}</Modal.Title>
                <ModalCloseButton onClick={onClose}>
                    <i className="fas fa-times"></i>
                </ModalCloseButton>
            </Modal.Header>
            <ModalBody style={{ padding: 0 }}>
                <Scrollbars height={HEIGHT}>
                    <h3 style={{ marginTop: 20 }}>{i18n('Bowtie interaction')}</h3>
                    <BarrierIcons>
                        {BARRIER_ITEMS.map((e, i) => (
                            <div key={i}>
                                {e.item} {i18n(e.description)}
                            </div>
                        ))}
                        {barrierTypes
                            ?.filter(barrierType => barrierType.barrierTypeSpecial !== 'UNMONITORED')
                            .map(
                                (barrierType, index) =>
                                    barrierType?.barrierTypeIcon?.iconReference && (
                                        <div key={index}>
                                            <Icon
                                                style={{ transform: 'scale(1.5)' }}
                                                type={barrierType?.barrierTypeIcon?.iconReference}
                                                scale="1.5"
                                                auxliarColor={theme['gray-900']}
                                                barrierTypeIcon={barrierTypeIconMap?.get(
                                                    barrierType?.barrierTypeIcon?.id
                                                )}
                                            />{' '}
                                            {barrierType?.name}
                                        </div>
                                    )
                            )}
                    </BarrierIcons>

                    <hr />

                    <h3>{i18n('Dashboard action items')}</h3>
                    <BarrierIcons>
                        {ACTIONS.map((e, i) => (
                            <div key={i}>
                                {e.item} {i18n(e.description)}
                            </div>
                        ))}
                    </BarrierIcons>

                    {extraInfo && (
                        <>
                            <hr />
                            <div dangerouslySetInnerHTML={{ __html: extraInfo }}></div>
                        </>
                    )}
                </Scrollbars>
            </ModalBody>
        </RoundedModal>
    )
}

export default memo(HelpModal)
